/**
 * Живые глаза пульта. Лента только ищет строку. Жмёт — только если стакан
 * биржи и кэф конторы ещё дают плюс, имена те же, и цифрам не больше двух секунд.
 */

import { Decimal } from 'decimal.js';

import { roiNet, SPORTS_FEE_RATE, takerCostFraction } from '../lib/calc';
import type { CloudLine } from './bot';
import { frameWhy, readFrame, type Bar } from './botBar';

export const LIVE_MS = 4000;
/** Прематч: книги живут дольше, чем лайв. */
export const PRE_LIVE_MS = 20_000;

export function liveWindow(line?: string | null): number {
  return line === 'prematch' ? PRE_LIVE_MS : LIVE_MS;
}

export interface LiveFill {
  /** Средняя цена набора. Для человека, не для ордера: FOK платит худший уровень. */
  price: Decimal;
  /** Худшая цена, без которой сумма не набирается. Потолок ордера смотрит сюда. */
  worst: Decimal;
  shares: Decimal;
  spent: Decimal;
}

export interface LiveNow {
  at: number;
  cloudOdds: string;
  polyCents: number;
  plusPct: string;
  thin: boolean;
  names: string[];
  line?: CloudLine;
  token?: string;
  /** Кэф конторы с котировки, не с ленты. Без него жать нельзя. */
  quoted?: boolean;
  /** True only for a freshly fetched CLOB book, never the feed fallback. */
  booked?: boolean;
}

export interface TapeWho {
  polyWho: string[];
  cloudWho: string[];
  polyName: string;
  cloudName: string;
  polySide: string;
  cloudSide: string;
}

export function normName(raw: string): string {
  return raw.toLowerCase().replace(/[^a-z0-9а-яё]+/gi, '');
}

/** Одни и те же двое. Пустые имена — не сходятся. */
export function sameEvent(left: string[], right: string[]): boolean {
  const a = left.map(normName).filter((bit) => bit.length >= 2);
  const b = right.map(normName).filter((bit) => bit.length >= 2);
  if (a.length === 0 || b.length === 0) return false;
  const hit = (x: string, y: string): boolean => x.includes(y) || y.includes(x);
  return a.every((one) => b.some((two) => hit(one, two))) && b.every((two) => a.some((one) => hit(two, one)));
}

/** Обе ноги на одну сторону — это не вилка. */
export function samePick(polyName: string, cloudName: string, polySide: string, cloudSide: string): boolean {
  const a = normName(polyName);
  const b = normName(cloudName);
  if (a.length >= 2 && a === b) return true;
  if (polySide !== '' && polySide === cloudSide && a.length >= 2 && b.length >= 2 && (a.includes(b) || b.includes(a))) {
    return true;
  }
  return false;
}

/** Сначала дешёвый аск. CLOB иногда отдаёт книгу с хвоста — иначе 99 ¢ вместо 48. */
export function sortAsks(
  asks: readonly (readonly [string, string])[],
): (readonly [string, string])[] {
  return [...asks].sort((left, right) => {
    try {
      return new Decimal(left[0]).cmp(new Decimal(right[0]));
    } catch {
      return 0;
    }
  });
}

/** Живой шар уехал от ленты: другой токен или чужой рынок. */
export const STRAY_CENTS = 20;

export function strayCents(live: number, tape: number | null | undefined): boolean {
  if (tape === null || tape === undefined || !Number.isFinite(tape) || !Number.isFinite(live)) {
    return false;
  }
  return Math.abs(live - tape) > STRAY_CENTS;
}

export function walkAsks(asks: readonly (readonly [string, string])[], usd: number): LiveFill | null {
  if (!(usd > 0) || asks.length === 0) return null;
  let spent = new Decimal(0);
  let shares = new Decimal(0);
  let worst = new Decimal(0);
  const budget = new Decimal(usd);
  for (const [px, sz] of sortAsks(asks)) {
    let price: Decimal;
    let size: Decimal;
    try {
      price = new Decimal(px);
      size = new Decimal(sz);
    } catch {
      continue;
    }
    if (!price.isFinite() || !size.isFinite() || price.lte(0) || size.lte(0)) continue;
    const room = budget.minus(spent);
    if (room.lte(0)) break;
    const take = Decimal.min(room.div(price), size);
    if (take.lte(0)) continue;
    shares = shares.plus(take);
    spent = spent.plus(take.times(price));
    worst = price;
    // Хвост меньше цента — не цена ордера. Иначе пыль после деления
    // помечает худшим следующий уровень, которого мы не покупаем.
    if (budget.minus(spent).lt('0.01')) break;
  }
  if (shares.lte(0) || spent.lte(0) || worst.lte(0)) return null;
  return { price: spent.div(shares), worst, shares, spent };
}

/** NET плюс в процентах, как на ленте. */
export function plusOf(polyPrice: Decimal, cloudOdds: Decimal, feeRate: string = SPORTS_FEE_RATE): Decimal | null {
  if (!polyPrice.isFinite() || polyPrice.lte(0) || polyPrice.gte(1)) return null;
  if (!cloudOdds.isFinite() || cloudOdds.lte(1)) return null;
  const polyOdds = new Decimal(1).div(polyPrice);
  try {
    return roiNet([polyOdds, cloudOdds], [takerCostFraction(polyPrice, feeRate), new Decimal(0)]).times(100);
  } catch {
    return null;
  }
}

export function cloudStakeOf(shares: Decimal, cloudOdds: Decimal, cap: Decimal): string | null {
  if (!shares.isFinite() || shares.lte(0) || !cloudOdds.isFinite() || cloudOdds.lte(1)) return null;
  const stake = shares.div(cloudOdds).toDecimalPlaces(2, Decimal.ROUND_HALF_UP);
  if (stake.lte(0) || stake.gt(cap)) return null;
  return stake.toFixed(2);
}

/** Стакан с ленты или один уровень из цены шара: без книги CLOB иначе кадр пустой. */
export function tapeAsksOf(
  book: readonly (readonly [string, string])[] | undefined,
  cents: number | null,
): (readonly [string, string])[] {
  if (Array.isArray(book) && book.length > 0) return [...book];
  if (cents === null || !Number.isFinite(cents) || cents <= 0 || cents >= 100) return [];
  try {
    const px = new Decimal(cents).div(100);
    if (!px.isFinite() || px.lte(0) || px.gte(1)) return [];
    return [[px.toFixed(4), '10000']];
  } catch {
    return [];
  }
}

export function nowOf(
  asks: readonly (readonly [string, string])[],
  cloudOdds: string,
  usd: number,
  at: number,
  names: string[] = [],
  line?: CloudLine,
  quoted = false,
): LiveNow | null {
  const fill = walkAsks(asks, usd);
  if (fill === null) return null;
  let odds: Decimal;
  try {
    odds = new Decimal(cloudOdds);
  } catch {
    return null;
  }
  // Плюс и цена ордера — по худшему уровню набора. Средняя рисует плюс там,
  // где FOK уже платит цену, на которой вилки нет.
  const plus = plusOf(fill.worst, odds);
  if (plus === null) return null;
  const cents = fill.worst.times(100).toDecimalPlaces(1, Decimal.ROUND_HALF_UP).toNumber();
  return {
    at,
    cloudOdds: odds.toFixed(odds.dp() > 3 ? 3 : Math.max(odds.dp(), 2)),
    polyCents: cents,
    plusPct: plus.toFixed(2),
    thin: fill.spent.lt(new Decimal(usd).minus('0.01')),
    names,
    ...(line ? { line } : {}),
    ...(quoted ? { quoted: true } : {}),
  };
}

/** Почему по живым числам жать нельзя. Пустая строка — можно. */
export function fitLive(
  now: LiveNow | null,
  tape: TapeWho,
  bar: Bar,
  clock: number,
  tapeCents: number | null = null,
  pending = false,
  line?: string | null,
): string {
  if (now === null) return pending ? 'ждём живой стакан' : 'нет живой цены';
  const windowMs = liveWindow(line);
  if (clock - now.at > windowMs) {
    return pending ? 'ждём живой стакан' : `живая цена старше ${windowMs / 1000} с`;
  }
  if (now.quoted && !sameEvent(now.names, tape.cloudWho)) return 'котировка конторы относится к другому матчу';
  if (!sameEvent(tape.polyWho, tape.cloudWho)) return 'имена ног не сходятся';
  if (samePick(tape.polyName, tape.cloudName, tape.polySide, tape.cloudSide)) {
    return 'обе ноги в одну сторону';
  }
  if (strayCents(now.polyCents, tapeCents)) {
    return `живой шар ${now.polyCents} ¢, на ленте ${tapeCents} ¢ — другой рынок`;
  }
  const frame = readFrame(bar);
  if (frame === null) return frameWhy(bar);
  const plus = new Decimal(now.plusPct);
  if (!plus.gt(0)) return 'сейчас плюс не больше 0%';
  if (plus.lt(frame.minPlus)) return `сейчас плюс меньше ${frame.minPlus.toFixed()}%`;
  if (plus.gt(frame.maxPlus)) return `сейчас плюс больше ${frame.maxPlus.toFixed()}%`;
  if (now.polyCents < frame.minCents.toNumber() || now.polyCents > frame.maxCents.toNumber()) {
    return `живой шар вне ${frame.minCents.toFixed()}–${frame.maxCents.toFixed()} ¢`;
  }
  if (now.thin) return 'живой стакан тоньше ставки';
  return '';
}

export function tapeWho(poly: { who: string[]; name: string; side: string }, cloud: { who: string[]; name: string; side: string }): TapeWho {
  return {
    polyWho: poly.who,
    cloudWho: cloud.who,
    polyName: poly.name,
    cloudName: cloud.name,
    polySide: poly.side,
    cloudSide: cloud.side,
  };
}
